// Localização: frontend/src/components/Agendamentos/FilterProfissional.jsx
// Componente flutuante para filtrar e navegar entre profissionais com scroll do mouse
// Exibe apenas as fotos dos profissionais alinhadas horizontalmente com efeito de carrossel infinito

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import api from '../../api/api';
import { getUsuarioFotoUrl } from '../../utils/imageUtils';

const PROFISSIONAIS_VISIVEIS = 5;
const FALLBACK_AVATAR = '/images/higeia-avatar-shield.png';

/**
 * @component FilterProfissional
 * @description Componente flutuante que exibe apenas fotos de profissionais com efeito de carrossel infinito
 * @param {function} onProfissionalSelect - Callback quando um profissional é selecionado
 * @param {function} onClose - Callback para fechar o componente
 * @param {function} onClearSelection - Callback para limpar a seleção do profissional
 * @param {object} selectedProfissional - Profissional atualmente selecionado (opcional)
 * @param {string} position - Posição do componente ('top-left', 'top-right', 'top-center', 'bottom-left', 'bottom-right')
 */
const FilterProfissional = ({ 
    onProfissionalSelect, 
    onClose,
    onClearSelection,
    selectedProfissional = null,
    position = 'top-center' 
}) => {
    const [profissionais, setProfissionais] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [startIndex, setStartIndex] = useState(0);
    const [profissionalSelecionado, setProfissionalSelecionado] = useState(null);
    const [isAnimating, setIsAnimating] = useState(false);
    const [direction, setDirection] = useState('forward');
    const containerRef = useRef(null);
    const scrollTimeoutRef = useRef(null);

    // Buscar profissionais ao montar o componente
    useEffect(() => {
        const fetchProfissionais = async () => {
            setIsLoading(true);
            try {
                const response = await api.get('/usuarios');
                // Filtrar apenas profissionais (roles que podem ter agendamentos)
                const profs = (response.data || []).filter(u => 
                    ['enfermeiro', 'medico', 'fisioterapeuta', 'cuidador', 'gerente', 'admin', 'proprietario'].includes(
                        u.role?.toLowerCase()
                    )
                );
                setProfissionais(profs);
            } catch (err) {
                console.error('Erro ao carregar profissionais:', err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchProfissionais();
    }, []);

    // Sincronizar profissional selecionado com prop externa
    useEffect(() => {
        if (selectedProfissional) {
            setProfissionalSelecionado(selectedProfissional.id);
        } else {
            setProfissionalSelecionado(null);
        }
    }, [selectedProfissional]);

    // Criar lista circular para efeito infinito
    const criarListaCircular = useCallback(() => {
        if (profissionais.length === 0) return [];
        
        const profissionaisVisiveis = [];
        for (let i = 0; i < PROFISSIONAIS_VISIVEIS; i++) {
            const index = (startIndex + i) % profissionais.length;
            profissionaisVisiveis.push({
                ...profissionais[index],
                originalIndex: index,
                displayIndex: i
            });
        }
        
        return profissionaisVisiveis;
    }, [profissionais, startIndex]);

    const profissionaisVisiveis = criarListaCircular();

    // Função para avançar
    const avancar = useCallback(() => {
        if (isAnimating || profissionais.length === 0) return;
        
        setIsAnimating(true);
        setDirection('forward');
        setStartIndex(prev => (prev + 1) % profissionais.length);
        
        setTimeout(() => {
            setIsAnimating(false);
        }, 300);
    }, [isAnimating, profissionais.length]);

    // Função para retroceder
    const retroceder = useCallback(() => {
        if (isAnimating || profissionais.length === 0) return;
        
        setIsAnimating(true);
        setDirection('backward');
        setStartIndex(prev => (prev - 1 + profissionais.length) % profissionais.length);
        
        setTimeout(() => {
            setIsAnimating(false);
        }, 300);
    }, [isAnimating, profissionais.length]);

    // Scroll com mouse
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleWheel = (e) => {
            e.preventDefault();
            
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }

            scrollTimeoutRef.current = setTimeout(() => {
                if (e.deltaY > 0) {
                    avancar();
                } else {
                    retroceder();
                }
            }, 50);
        };

        container.addEventListener('wheel', handleWheel, { passive: false });

        return () => {
            container.removeEventListener('wheel', handleWheel);
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, [avancar, retroceder]);

    // Função para selecionar um profissional
    const handleProfissionalClick = (profissional) => {
        if (profissionalSelecionado === profissional.id) {
            return;
        }
        setProfissionalSelecionado(profissional.id);
        if (onProfissionalSelect) {
            onProfissionalSelect(profissional);
        }
    };

    // Função para limpar a seleção
    const handleClearSelection = (e) => {
        e.stopPropagation();
        setProfissionalSelecionado(null);
        if (onClearSelection) {
            onClearSelection();
        }
    };

    // Determinar classes de posição
    const getPositionClasses = () => {
        const positions = {
            'top-left': 'top-20 left-4',
            'top-right': 'top-20 right-4',
            'top-center': 'top-32 left-1/2 -translate-x-1/2', // Abaixo do FilterPaciente
            'bottom-left': 'bottom-4 left-4',
            'bottom-right': 'bottom-4 right-4'
        };
        return positions[position] || positions['top-center'];
    };

    if (isLoading || profissionais.length === 0) {
        return null;
    }

    return (
        <div 
            ref={containerRef}
            className={`fixed ${getPositionClasses()} z-50 flex items-center gap-3 px-4 py-2 overflow-visible`}
        >
            {profissionaisVisiveis.map((profissional, index) => {
                const isSelected = profissionalSelecionado === profissional.id;
                const isFirst = index === 0;
                const isLast = index === PROFISSIONAIS_VISIVEIS - 1;
                
                const getAnimationClass = () => {
                    if (!isAnimating) return '';
                    
                    if (direction === 'forward') {
                        if (isFirst) return 'animate-slide-out-left';
                        if (isLast) return 'animate-slide-in-right';
                    } else {
                        if (isLast) return 'animate-slide-out-right';
                        if (isFirst) return 'animate-slide-in-left';
                    }
                    
                    return '';
                };
                
                return (
                    <div
                        key={`${profissional.id}-${profissional.originalIndex}-${startIndex}`}
                        onClick={() => handleProfissionalClick(profissional)}
                        className={`
                            relative cursor-pointer transition-all duration-300
                            ${getAnimationClass()}
                        `}
                        title={profissional.nome_completo || 'Profissional'}
                    >
                        <div className="relative group">
                            <img
                                className={`w-16 h-16 rounded-full object-cover border-2 transition-all duration-300 group-hover:scale-125 group-hover:shadow-xl ${
                                    isSelected 
                                        ? 'border-emerald-500 shadow-lg ring-2 ring-emerald-300' 
                                        : 'border-white shadow-md group-hover:border-emerald-300'
                                }`}
                                src={getUsuarioFotoUrl(profissional.fotoURL)}
                                alt={`Foto de ${profissional.nome_completo || 'Profissional'}`}
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = FALLBACK_AVATAR;
                                }}
                            />
                            {isSelected && (
                                <button
                                    onClick={handleClearSelection}
                                    className="absolute -top-1 -right-1 z-20 p-1 bg-red-500 hover:bg-red-600 rounded-full shadow-lg transition-all duration-200 hover:scale-110 flex items-center justify-center"
                                    title="Remover filtro de profissional"
                                    aria-label="Remover filtro de profissional"
                                >
                                    <X className="h-3 w-3 text-white" />
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}
            
            <style>{`
                @keyframes slideOutLeft {
                    from {
                        opacity: 1;
                        transform: translateX(0) scale(1);
                    }
                    to {
                        opacity: 0;
                        transform: translateX(-100px) scale(0.7);
                    }
                }
                
                @keyframes slideInRight {
                    from {
                        opacity: 0;
                        transform: translateX(100px) scale(0.7);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0) scale(1);
                    }
                }
                
                @keyframes slideOutRight {
                    from {
                        opacity: 1;
                        transform: translateX(0) scale(1);
                    }
                    to {
                        opacity: 0;
                        transform: translateX(100px) scale(0.7);
                    }
                }
                
                @keyframes slideInLeft {
                    from {
                        opacity: 0;
                        transform: translateX(-100px) scale(0.7);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0) scale(1);
                    }
                }
                
                .animate-slide-out-left {
                    animation: slideOutLeft 0.3s ease-out forwards;
                }
                
                .animate-slide-in-right {
                    animation: slideInRight 0.3s ease-out forwards;
                }
                
                .animate-slide-out-right {
                    animation: slideOutRight 0.3s ease-out forwards;
                }
                
                .animate-slide-in-left {
                    animation: slideInLeft 0.3s ease-out forwards;
                }
            `}</style>
        </div>
    );
};

export default FilterProfissional;

